import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core';
import {Router}                       from '@angular/router';
import {ActivatedRoute}               from '@angular/router';
import {StsPortalStatusService}       from '../../../services/sts-portal-status.service';
import {StsTenantService}             from '../../../services/sts-tenant.service';
import {StsHttpService}               from '../../../services/sts-http.service';
import {StsSessionTimeoutService}     from '../../../services/sts-session-timeout.service';
import {StsChangePassword}            from '../../../models/sts-change-password.model';
import {StsNotification}              from '../../../models/sts-notification.model';
import {StsNotificationType}          from '../../../models/sts-notification-type';
import {StsPortalStatus}              from '../../../models/sts-portal-status';
import {StsButtonComponent}           from 'app/components/common/sts-button/sts-button.component';
import {StsUserService} from '../../../services/sts-user.service';
import {StsCmsService} from '../../../services/sts-cms-service.service';
import {forkJoin} from 'rxjs';

@Component({
  selector: 'app-account-password',
  templateUrl: './account-password.component.html',
  styleUrls: ['./account-password.component.scss']
})
export class AccountPasswordComponent implements OnInit, OnDestroy {

  pageLoaded = false;
  formSubmitSuccess = false;
  stsPortalStatus: StsPortalStatus = null;
  setNewPasswordFlow: boolean;
  answersProvided: boolean;
  stsChangePassword: StsChangePassword = new StsChangePassword();
  existingPasswordValid = true;
  newPasswordValid = true;
  confirmPasswordValid = true;
  blacklistKeys = [];
  blacklistTexts = [];
  Rules = [];
  pwdRules = [];
  isDisclaimerRequired: boolean;
  securityQuestionsActive: boolean;

  public errors: StsNotification[] = [];
  public errorTexts: StsNotification[] = [];

  @ViewChild(StsButtonComponent) submitButton;

  constructor(private stsHttp: StsHttpService
    , private portalStatusService: StsPortalStatusService
    , private tenantService: StsTenantService
    , private stsSessionTimeoutService: StsSessionTimeoutService
    , private userService: StsUserService
    , private cmsService: StsCmsService
    , private route: ActivatedRoute
    , private router: Router) {
  }

  ngOnDestroy() {
    this.stsSessionTimeoutService.cancelTimeout();
  }

  ngOnInit() {
    this.stsSessionTimeoutService.setTimeout();

    this.setNewPasswordFlow = false;
    this.getStatusAndPasswordPolicy();
  }

  private getStatusAndPasswordPolicy() {
    this.route.params.subscribe(params => {
      this.portalStatusService.getPortalStatus().subscribe(
        (portalStatus: StsPortalStatus) => {
          this.pageLoaded = true;
          this.stsPortalStatus = portalStatus;
          this.answersProvided = portalStatus.answersProvided;
          if (params['new'] === 'new') {
            if (!this.stsPortalStatus.isSelfRegFlow && !this.stsPortalStatus.isInRecoveryMode) {
              console.log('User attempting to execute \'set password flow\' wihtout permission! Redirecting to login.');
              this.router.navigateByUrl('/account/signin');
              return;
            }
            this.setNewPasswordFlow = true;
          }

          // user is not creating a new password for the first time, and login status required pass change,
          // it means that its updating its expired password.
          // Otherwise, user decided to change password, and no warning message is required
          if (!this.setNewPasswordFlow && params['new'] === 'change') {
            // warn user that password is exipred and it must be updated
            let error = new StsNotification();
            error.code = 'sts-expired-password';
            error.description = 'Your password is expired. Please set up a new password.';
            error.level = StsNotificationType.Warning;
            this.errors.push(error);
          }

          this.tenantService.getTenantSource().subscribe(tenant => {
            {
              if (tenant.permittedBlacklistKeys) {
                this.blacklistKeys = tenant.permittedBlacklistKeys.split(',').map(s => `PASSWORDRULE_${s}`);
                this.securityQuestionsActive = tenant.passwordRecoveryChoices.indexOf('Security Questions') > -1;
                this.isDisclaimerRequired = tenant.isDisclaimerRequired;
            }
          }
          });

          this.userService.getPasswordPolicy(params['id']).subscribe(resp => {
            if (resp.success) {

              this.pwdRules = resp.passwordRules;

              // validate integrity
              let faulty = this.pwdRules.find(o => o.range != null && o.range.atLeast == null && o.range.notMoreThan == null);
              if (faulty) {
                let errText = `Password policy for ${faulty.passwordRuleCode} has invalid range, use ariel security admin for configuration.`;
                console.log(errText);
                let error = new StsNotification();
                error.code = errText;
                error.description = errText;
                error.level = StsNotificationType.Error;
                this.errors.push(error);
                return;
              }

              let requests = [];
              // for each password rule, read corresponding label from CMS
              this.pwdRules.forEach(o => {
                requests.push(this.cmsService.getText(o.passwordRuleCode));
              });
              // after all labels are read, replace placeholders with policy values
              forkJoin(requests).subscribe(res => {

                res.forEach((o, i) => {
                  if (!o) { console.log(`Error: Missing cms label for ${this.pwdRules[i].passwordRuleCode}`); }
                  if (this.pwdRules[i].hasOwnProperty('range')) {
                  res[i] = (o || '') && o.replace('#atLeast', this.pwdRules[i].range.atLeast)
                  .replace('#notMoreThan', this.pwdRules[i].range.notMoreThan);
                }
                this.pwdRules[i].Text = res[i];
                });

                let blklstKys = this.blacklistKeys;
                let blklstTexts = [];
                let whtlstTexts = [];
                this.pwdRules.forEach(function(i) {
                  if (blklstKys.indexOf(i.passwordRuleCode.replace('USERDEFINED_', '')) === -1) {
                    whtlstTexts.push(i.Text);
                  } else {
                    blklstTexts.push(i.Text);
                  }
                });
                this.Rules = whtlstTexts;
                this.blacklistTexts = blklstTexts;
              });

            } else {
              console.log(`failed to get password policy from ariel security for user id: ${params['id']}`);
            }
          });

        }
      );
    });
  }

  validateFields(submitted = false) {
    let existingPasswordValid = this.setNewPasswordFlow || !!this.stsChangePassword.existingPassword;
    let newPasswordValid = !!this.stsChangePassword.newPassword;
    let confirmPasswordValid = !!this.stsChangePassword.confirmPassword;
    if (!submitted) {
      return existingPasswordValid && newPasswordValid && confirmPasswordValid;
    }

    this.existingPasswordValid = existingPasswordValid;
    this.newPasswordValid = newPasswordValid;
    this.confirmPasswordValid = confirmPasswordValid;
    return this.existingPasswordValid && this.newPasswordValid && this.confirmPasswordValid;
  }

  cancel(): void {
    if (this.stsPortalStatus.isSelfRegFlow) {
      window.location.href = '/';
    } else {
      document.location.href = '/account/signout';
    }
  }

  onContinue() {
    window.location.href = '/';
  }

  gotoNextStep() {
    if (this.securityQuestionsActive) {
      this.router.navigateByUrl('/account/questions/new');
    } else if (this.stsPortalStatus.isSelfRegFlow && this.isDisclaimerRequired) {
      // show disclaimer only during self-registration and if required by tenant
      this.router.navigateByUrl('/account/disclaimer');
    } else {
      // redirect to login page
      this.router.navigateByUrl('/account/signin');
    }
  }

  onSubmit() {
    this.errors = [];

    if (!this.validateFields(true)) {
      return;
    }

    if (this.stsChangePassword.newPassword !== this.stsChangePassword.confirmPassword) {
      let error = new StsNotification();
      error.code = 'sts-change-pass-confirmation-fail';
      error.description = 'Password confirmation doesn\'t match!';
      error.level = StsNotificationType.Error;
      this.errors.push(error);
      return;
    }

    let url = '';
    let requestBody = {};
    if (this.setNewPasswordFlow) {
      url = '/api/users/my/passwordassignment';
      requestBody = {
        NewPassword: this.stsChangePassword.newPassword
      };
    } else {
      url = '/api/users/my/passwordchange';
      requestBody = {
        OldPassword: this.stsChangePassword.existingPassword,
        NewPassword: this.stsChangePassword.newPassword
      };
    }

    this.submitButton.onSubmit();
    this.stsHttp.post(url, requestBody).subscribe((data) => {
        this.errors = [];
        this.errorTexts = [];
        this.formSubmitSuccess = true;

        if (this.setNewPasswordFlow) {

          if (this.stsPortalStatus.isSelfRegFlow) {
            this.gotoNextStep();
          } else if (this.stsPortalStatus.isInRecoveryMode) {
            // user answered security questions or security questions are deactivated
            if (this.stsPortalStatus.answersProvided || !this.securityQuestionsActive) {
              let e = new StsNotification();
              e.code = 'sts-reset-password-success';
              e.level = StsNotificationType.Success;
              e.description = 'You have successfully changed your password.';
              this.errors.push(e);
              // console.log("In recovery mode, seq questions already answered, logging user in...");
              this.submitButton.onSubmitFinished();
              return;
            }

            this.gotoNextStep();
          }

          return;
        }

        this.submitButton.onSubmitFinished();
        let err = new StsNotification();
        err.code = 'sts-change-password-success';
        err.level = StsNotificationType.Success;
        err.description = 'Your password has been changed';
        this.errors.push(err);

      },
      err => {
        this.errors = [];
        this.errorTexts = [];
        this.submitButton.onSubmitFinished();
        if (err.error && err.error.errors) {
          for (let i in err.error.errors) {
            console.log(i, err.error.errors[i]);
            let pwdRule = this.pwdRules.find(o => o.passwordRuleCode === err.error.errors[i].errorCode);
            // if Ariel Security returns error, that is not listed in password rules, we add it to separate param
            if (!pwdRule || (pwdRule && this.blacklistKeys.indexOf(pwdRule.passwordRuleCode.replace('USERDEFINED_', '')) !== -1)) {
              let error = new StsNotification();
              error.code = err.error.errors[i].errorCode;
              if(error.code === 'PASSWORDRULE_VALIDOLDPASSWORD')
                  error.level = StsNotificationType.Error;
              else
                  error.level = StsNotificationType.PasswordPolicyError;
              error.description = pwdRule?pwdRule.Text:undefined;
              this.errors.push(error);
            } else {
                let errorText = new StsNotification();
                errorText.code = err.error.errors[i].errorCode;
                errorText.description = pwdRule?pwdRule.Text:undefined;
                if(errorText.code === 'PASSWORDRULE_VALIDOLDPASSWORD')
                  errorText.level = StsNotificationType.Error;
                else
                  errorText.level = StsNotificationType.PasswordPolicyError;
                if (errorText.description) { // if cms is missing a label, the text will be empty; we do not show empty error
                  this.errorTexts.push(errorText);
                }
              }
          }
        } else {
          let error = new StsNotification();
          error.code = 'sts-change-pass-message';
          error.description = 'An error has occurred!';
          error.level = StsNotificationType.Error;
          this.errors.push(error);
        }

      });

  }

}
